/**
 * Preflight self-check (§7.9, FR-20, FR-45).
 *
 * FR-45 sets an unusually high bar for what is normally a throwaway diagnostic:
 * **"the user must be able to run this the day before the draft and trust the
 * result."** Two consequences shaped this module.
 *
 * First, a check that cannot fail is worse than no check, because it launders
 * ignorance into confidence. Every check here can genuinely fail, and anything
 * it could not actually verify reports `unknown` rather than `pass`.
 *
 * Second, "trust the result" means the failure has to be *actionable the night
 * before*. Every non-passing check carries a `fix` naming the specific thing to
 * do, because a red line that says "ranking source degraded" at 11pm is not
 * help.
 *
 * Pure module — the caller gathers the facts, this decides what they mean.
 */

import type { AdpData, LeagueConfig, RankingSource } from './types.ts';
import type { MatchReport } from './matching.ts';

export type CheckStatus = 'pass' | 'warn' | 'fail' | 'unknown';

export interface Check {
  id: string;
  /** Short label for the checklist line. */
  label: string;
  status: CheckStatus;
  /** What was actually observed. Never a restatement of the label. */
  detail: string;
  /** What to do about it. Present whenever status is not 'pass'. */
  fix: string | null;
  /** Requirement this check exists to defend. */
  requirement: string;
}

export interface DoctorInput {
  league: LeagueConfig | null;
  sources: RankingSource[];
  /** Adapter-neutral load diagnostics, including sources that failed entirely. */
  sourceDiagnostics?: {
    source_id: string;
    source_name: string;
    status: 'ok' | 'degraded' | 'failed' | 'disabled' | 'skipped';
    rows: number;
    message: string | null;
  }[];
  /** Published Blueprint metadata, when the app has attempted to load it. */
  blueprint?: {
    status: 'ready' | 'disabled';
    model_version: string | null;
    generated_at: string | null;
    expires_at: string | null;
    message: string | null;
  };
  /** Per-source match reports (FR-20). */
  matchReports: MatchReport[];
  adp: AdpData | null;
  playerCache: {
    loaded: boolean;
    count: number;
    age_hours: number | null;
    stale: boolean;
  };
  /** Result of contacting Sleeper, or null if not attempted. */
  sleeperReachable: boolean | null;
  /** Parsed /api/health, or null when the Workers are absent (FR-66). */
  health: { url_fetch_enabled?: boolean; degraded?: string[]; status?: string } | null;
  /** How many recommendations the engine produced on the current board. */
  recommendationCount: number | null;
  now?: Date;
}

export interface DoctorReport {
  checks: Check[];
  passed: number;
  warnings: number;
  failures: number;
  /** True when nothing failed. Warnings are survivable; failures are not. */
  ok: boolean;
  /** One line the user can act on, or a clean bill of health. */
  headline: string;
}

/** Acceptance criterion 4. */
const MATCH_RATE_TARGET = 0.98;
const STALE_SOURCE_DAYS = 7;

export function runDoctor(input: DoctorInput): DoctorReport {
  const now = input.now ?? new Date();
  const checks: Check[] = [];

  // --- Sleeper reachability (FR-4) ----------------------------------------
  checks.push(
    input.sleeperReachable === null
      ? {
          id: 'sleeper',
          label: 'Sleeper API reachable',
          status: 'unknown',
          detail: 'Not checked.',
          fix: 'Run the check with a network connection available.',
          requirement: 'FR-4',
        }
      : input.sleeperReachable
        ? {
            id: 'sleeper',
            label: 'Sleeper API reachable',
            status: 'pass',
            detail: 'Responded normally.',
            fix: null,
            requirement: 'FR-4',
          }
        : {
            id: 'sleeper',
            label: 'Sleeper API reachable',
            status: 'fail',
            detail: 'Could not reach api.sleeper.app.',
            fix: 'Check your connection. If Sleeper itself is down, the board still works from cache and manual pick entry (press "d" on a player) covers live sync.',
            requirement: 'FR-4',
          },
  );

  // --- Player cache (§4.1, FR-4) ------------------------------------------
  if (!input.playerCache.loaded) {
    checks.push({
      id: 'player-cache',
      label: 'Player list cached',
      status: 'fail',
      detail: 'No player list stored in this browser.',
      fix: 'Open the app and complete onboarding once — it downloads the list (about 14MB) and caches it for 24 hours.',
      requirement: '§4.1',
    });
  } else if (input.playerCache.stale) {
    checks.push({
      id: 'player-cache',
      label: 'Player list cached',
      status: 'warn',
      detail: `${input.playerCache.count.toLocaleString()} players, ${formatAge(input.playerCache.age_hours)} old.`,
      fix: 'It will refresh automatically on next load. Refresh before draft day so rookies and team changes are current.',
      requirement: '§4.1',
    });
  } else {
    checks.push({
      id: 'player-cache',
      label: 'Player list cached',
      status: 'pass',
      detail: `${input.playerCache.count.toLocaleString()} players, ${formatAge(input.playerCache.age_hours)} old.`,
      fix: null,
      requirement: '§4.1',
    });
  }

  // --- At least one ranking source (FR-4, §4.6) ---------------------------
  const loaded = input.sources.filter((s) => s.rows.length > 0);
  checks.push(
    loaded.length === 0
      ? {
          id: 'sources',
          label: 'Ranking source loaded',
          status: 'fail',
          detail: 'No ranking source has any rows.',
          fix: 'This should be impossible — Sleeper\'s own ordering is derived from the cached player list. Refresh the player cache.',
          requirement: 'FR-4, §4.6',
        }
      : {
          id: 'sources',
          label: 'Ranking source loaded',
          status: 'pass',
          detail: `${loaded.length} loaded: ${loaded.map((s) => `${s.source_name} (${s.rows.length} rows, as of ${s.as_of_date})`).join(', ')}.`,
          fix: null,
          requirement: 'FR-4, §4.6',
        },
  );

  if (input.sourceDiagnostics) {
    const unavailable = input.sourceDiagnostics.filter((source) => source.status !== 'ok');
    checks.push({
      id: 'source-health',
      label: 'Source health',
      status: unavailable.length === 0 ? 'pass' : 'warn',
      detail: input.sourceDiagnostics
        .map((source) => `${source.source_name}: ${source.status} (${source.rows} rows)${source.message ? ` — ${source.message}` : ''}`)
        .join(' · '),
      fix: unavailable.length === 0
        ? null
        : 'Review the named source messages. The board continues with the sources that loaded, and you can use paste mode or refresh before drafting.',
      requirement: '§4.6',
    });
  }

  // --- Match rates (FR-20, acceptance criterion 4) ------------------------
  // The check most likely to save a draft, and the one most worth doing early:
  // every unmatched name is a player silently missing from the board, and each
  // is fixable in seconds the night before and not at all during the draft.
  if (input.matchReports.length === 0) {
    checks.push({
      id: 'match-rate',
      label: 'Name match rate',
      status: 'unknown',
      detail: 'No external ranking source has been imported, so nothing needed matching.',
      fix: 'Import your ranking list now rather than on draft night — that is when unmatched names get found.',
      requirement: 'FR-20',
    });
  } else {
    for (const report of input.matchReports) {
      const rate = report.match_rate;
      const pct = `${(rate * 100).toFixed(1)}%`;
      const worst = report.unmatched.slice(0, 5).map((u) => u.raw_name).join(', ');

      checks.push({
        id: `match-rate:${report.source_id}`,
        label: `Name match rate — ${report.source_id}`,
        status: rate >= MATCH_RATE_TARGET ? 'pass' : rate >= 0.9 ? 'warn' : 'fail',
        detail:
          rate >= MATCH_RATE_TARGET
            ? `${pct} of ${report.rows_read} rows resolved.`
            : `${pct} of ${report.rows_read} rows resolved — ${report.rows_unmatched} unmatched${worst ? `, including: ${worst}` : ''}.`,
        fix:
          rate >= MATCH_RATE_TARGET
            ? null
            : 'Open the source, review the unmatched names, and click the right player for each. Corrections are saved and apply to every future import.',
        requirement: 'FR-20, acceptance criterion 4',
      });
    }
  }

  // --- Source freshness (§4.2) --------------------------------------------
  const staleSources = input.sources.filter((s) => {
    const asOf = new Date(s.as_of_date);
    if (Number.isNaN(asOf.getTime())) return false;
    return (now.getTime() - asOf.getTime()) / 86_400_000 > STALE_SOURCE_DAYS;
  });

  checks.push(
    staleSources.length === 0
      ? {
          id: 'freshness',
          label: 'Sources are current',
          status: 'pass',
          detail: `All ${input.sources.length} sources are within ${STALE_SOURCE_DAYS} days: ${input.sources.map((s) => `${s.source_name} (${s.as_of_date})`).join(', ')}.`,
          fix: null,
          requirement: '§4.2',
        }
      : {
          id: 'freshness',
          label: 'Sources are current',
          status: 'warn',
          detail: `${staleSources.map((s) => `${s.source_name} (${s.as_of_date})`).join(', ')} older than ${STALE_SOURCE_DAYS} days.`,
          fix: 'Re-import or re-fetch these before the draft. Analysts update constantly through preseason, and a week-old list has missed real news.',
          requirement: '§4.2',
        },
  );

  if (input.blueprint) {
    const blueprint = input.blueprint;
    const ready = blueprint.status === 'ready';
    checks.push({
      id: 'blueprint',
      label: 'Blueprint forecast',
      status: ready ? 'pass' : 'warn',
      detail: ready
        ? `${blueprint.model_version ?? 'Current model'} generated ${blueprint.generated_at ?? 'on an unknown date'} and expires ${blueprint.expires_at ?? 'on an unknown date'}.`
        : `No valid Blueprint snapshot is available${blueprint.message ? ` — ${blueprint.message}` : '.'}`,
      fix: ready
        ? null
        : 'Use Blend while Blueprint is unavailable. Publish or refresh the validated forecast snapshot before relying on Blueprint recommendations.',
      requirement: 'Blueprint mode',
    });
  }

  // --- League configuration (FR-2) ----------------------------------------
  if (!input.league) {
    checks.push({
      id: 'league',
      label: 'League settings resolved',
      status: 'fail',
      detail: 'No league loaded.',
      fix: 'Complete onboarding: enter your Sleeper username and pick your league.',
      requirement: 'FR-2',
    });
  } else {
    const hasSlot = input.league.draft_slot !== null;
    checks.push({
      id: 'league',
      label: 'League settings resolved',
      status: hasSlot ? 'pass' : 'warn',
      detail: hasSlot
        ? `${input.league.teams}-team, ${input.league.rounds} rounds, drafting from slot ${input.league.draft_slot}.`
        : `${input.league.teams}-team, ${input.league.rounds} rounds — your draft slot could not be resolved.`,
      fix: hasSlot
        ? null
        : 'Sleeper reports draft order only once it is set. Until then, roster-aware advice and "your next pick" are off; everything else works.',
      requirement: 'FR-2',
    });
  }

  // --- ADP (FR-31) ---------------------------------------------------------
  checks.push(
    input.adp === null
      ? {
          id: 'adp',
          label: 'ADP available',
          status: 'warn',
          detail: 'No ADP loaded.',
          fix: 'The /api/adp Worker may be disabled or removed. "Will he last?" estimates and bye weeks are off; the board and value engine are unaffected.',
          requirement: 'FR-31, §4.4',
        }
      : input.adp.total_drafts < 100
        ? {
            id: 'adp',
            label: 'ADP available',
            status: 'warn',
            detail: `Only ${input.adp.total_drafts.toLocaleString()} drafts in the sample (as of ${input.adp.as_of_date}).`,
            fix: 'Thin sample — treat availability estimates as hints. It thickens as preseason drafts accumulate.',
            requirement: 'FR-31',
          }
        : {
            id: 'adp',
            label: 'ADP available',
            status: 'pass',
            detail: `${input.adp.total_drafts.toLocaleString()} drafts, as of ${input.adp.as_of_date}.`,
            fix: null,
            requirement: 'FR-31',
          },
  );

  // --- Workers (FR-66) -----------------------------------------------------
  // Absent Workers are explicitly fine — the app is required to work without
  // them — so this reports rather than fails.
  checks.push(
    input.health === null
      ? {
          id: 'workers',
          label: 'Pages Functions',
          status: 'warn',
          detail: 'No /api/health response — the Workers are absent or disabled.',
          fix: 'Expected on a static-only deploy. ADP and URL fetching are off; Sleeper, FantasyCalc, and paste-mode rankings all still work.',
          requirement: 'FR-66',
        }
      : {
          id: 'workers',
          label: 'Pages Functions',
          status: (input.health.degraded?.length ?? 0) > 0 ? 'warn' : 'pass',
          detail:
            (input.health.degraded?.length ?? 0) > 0
              ? `Degraded: ${input.health.degraded!.join(', ')}.`
              : `Healthy. URL fetching ${input.health.url_fetch_enabled ? 'enabled' : 'disabled'}.`,
          fix:
            (input.health.degraded?.length ?? 0) > 0
              ? 'Check the named endpoints. Paste-mode rankings work regardless.'
              : null,
          requirement: 'FR-66',
        },
  );

  // --- Engine sanity (FR-27) ----------------------------------------------
  checks.push(
    input.recommendationCount === null
      ? {
          id: 'engine',
          label: 'Recommendation engine',
          status: 'unknown',
          detail: 'Not exercised — no draft loaded.',
          fix: 'Load your draft to check the engine end to end.',
          requirement: 'FR-27',
        }
      : input.recommendationCount >= 5
        ? {
            id: 'engine',
            label: 'Recommendation engine',
            status: 'pass',
            detail: `Produced ${input.recommendationCount} recommendations with component breakdowns.`,
            fix: null,
            requirement: 'FR-27',
          }
        : {
            id: 'engine',
            label: 'Recommendation engine',
            status: input.recommendationCount > 0 ? 'warn' : 'fail',
            detail: `Produced only ${input.recommendationCount} recommendations.`,
            fix: 'Expected near the very end of a draft. Earlier than that, it means the available pool is nearly empty — check that your ranking source loaded.',
            requirement: 'FR-27',
          },
  );

  const passed = checks.filter((c) => c.status === 'pass').length;
  const warnings = checks.filter((c) => c.status === 'warn').length;
  const failures = checks.filter((c) => c.status === 'fail').length;

  return {
    checks,
    passed,
    warnings,
    failures,
    ok: failures === 0,
    headline: headlineFor(checks, failures, warnings),
  };
}

function headlineFor(checks: Check[], failures: number, warnings: number): string {
  if (failures > 0) {
    const first = checks.find((c) => c.status === 'fail')!;
    return `${failures} problem${failures === 1 ? '' : 's'} to fix before draft day. Start with: ${first.label} — ${first.fix ?? first.detail}`;
  }
  if (warnings > 0) {
    const first = checks.find((c) => c.status === 'warn')!;
    return `Ready to draft, with ${warnings} thing${warnings === 1 ? '' : 's'} worth knowing. Most important: ${first.label} — ${first.detail}`;
  }
  return 'Ready to draft. Every check passed.';
}

function formatAge(hours: number | null): string {
  if (hours === null) return 'unknown age';
  if (hours < 1) return 'under an hour';
  if (hours < 48) return `${Math.round(hours)} hours`;
  return `${Math.round(hours / 24)} days`;
}

/** Render the report as a plain-text checklist, for the CLI and for copy/paste. */
export function formatDoctorReport(report: DoctorReport): string {
  const icon = (s: CheckStatus): string =>
    s === 'pass' ? '  OK  ' : s === 'warn' ? ' WARN ' : s === 'fail' ? ' FAIL ' : '  ??  ';

  const lines = ['draftpuppy doctor', ''];
  for (const check of report.checks) {
    lines.push(`[${icon(check.status)}] ${check.label}`);
    lines.push(`           ${check.detail}`);
    if (check.fix) lines.push(`           → ${check.fix}`);
    lines.push('');
  }
  lines.push(
    `${report.passed} passed, ${report.warnings} warning${report.warnings === 1 ? '' : 's'}, ${report.failures} failure${report.failures === 1 ? '' : 's'}.`,
  );
  lines.push(report.headline);
  return lines.join('\n');
}
